import find from 'lodash/find';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';

import { Checkbox } from 'src/presentation/components/agent_desktop_components';
import ConditionRow from './ConditionRow';
import {
  ConditionType,
  AppPlatformDataAttributeStringCondition as AppPlatformDataAttributeStringConditionType,
  CustomAttributeStringCondition as CustomAttributeConditionType,
  StringMatcherCondition as StringMatcherConditionType,
} from 'src/actions/rules/condition';
import InputField from 'src/presentation/components/forms/InputField';
import { DropdownMenu } from 'src/presentation/components/agent_desktop_components';
import StringMatcher, { MatcherType } from 'src/actions/rules/string_matcher';
import { StyledConditionLabel } from '../shared/StyledComponents';
import useAppPlatformDataAttributes from '../hooks/useAppPlatformDataAttributes';
import useCustomAttributes from '../hooks/useCustomAttributes';

enum Contains {
  True = 'TRUE',
  False = 'FALSE',
}

type StringMatcherConditionProps = {
  condition: StringMatcherConditionType;
  index: number;
  isReadOnly?: boolean;
  onDelete: Function;
  onUpdate: Function;
};

const StringMatcherCondition: FC<StringMatcherConditionProps> = ({
  condition,
  index,
  isReadOnly,
  onDelete,
  onUpdate,
}) => {
  const matcher = condition.value;
  const value = convertMatcherValueToInputString(matcher.getValue());

  const onChangeValue = useCallback(
    (value = '') => {
      matcher.setValue(convertInputStringToMatcherValue(value, matcher));
      condition.setMatcher(matcher);
      onUpdate({ condition, index });
    },
    [condition, matcher, index, onUpdate]
  );

  const onChangeMatchEvaluator = useCallback(
    type => {
      matcher.setType(type);
      condition.setMatcher(matcher);
      onUpdate({ condition, index });
    },
    [condition, matcher, index, onUpdate]
  );

  const onChangeNot = useCallback(
    contains => {
      const not = contains === Contains.False;
      matcher.setNot(not);
      condition.setMatcher(matcher);
      onUpdate({ condition, index });
    },
    [condition, matcher, index, onUpdate]
  );

  const onChangeCaseSensitive = useCallback(() => {
    matcher.setCaseSensitive(!matcher.getCaseSensitive());
    condition.setMatcher(matcher);
    onUpdate({ condition, index });
  }, [condition, matcher, index, onUpdate]);

  const conditionNames = { ...defaultConditionNames };
  const customAttributes = useCustomAttributes();
  customAttributes.forEach(({ attr, label }) => {
    conditionNames[attr] = label;
  });
  const appPlatformDataAttributes = useAppPlatformDataAttributes({ fetch: false });
  appPlatformDataAttributes.forEach(({ attrId, label }) => {
    conditionNames[attrId] = label;
  });

  if (isReadOnly) {
    return (
      <>
        <StyledRow index={index} isReadOnly onDelete={onDelete}>
          <SelectorRow>
            <StyledConditionLabel disabled data-aid="stringMatchCondition-description">
              {renderConditionLabel(condition, conditionNames)}
            </StyledConditionLabel>
            <StyledContainsLabel data-aid="readOnlyStringMatcherContains">{renderContainsLabel()}</StyledContainsLabel>
            <StyledConditionLabel data-aid="readOnlyStringMatcherMatchLabel">{renderMatchLabel()}</StyledConditionLabel>
          </SelectorRow>
        </StyledRow>
        <StyledConditionValues data-aid="readOnlyStringMatcherValues">{matcher.getValue()}</StyledConditionValues>
        <StyledCaseSensitive data-aid="readOnlyStringMatcherCaseSensitive">
          {renderCaseSensitiveText()}
        </StyledCaseSensitive>
      </>
    );
  }

  return (
    <StyledRow index={index} onDelete={onDelete}>
      <Wrapper>
        <SelectorRow>
          <StyledConditionLabel disabled data-aid="stringMatchCondition-description">
            {renderConditionLabel(condition, conditionNames)}
          </StyledConditionLabel>
          <ContainsOrNotSelector
            data-aid="stringMatchCondition-containsSelector"
            contains={!matcher.hasNot()}
            onChange={onChangeNot}
          />
          <MatchEvaluator
            data-aid="stringMatchCondition-matchEvaluator"
            matcher={matcher}
            onChange={onChangeMatchEvaluator}
          />
        </SelectorRow>
        <StringMatcherInput
          data-aid="stringMatchCondition-input"
          attr={`conditions[${index}].value.${matcher.getValuePath()}`}
          placeholder={getPlaceholder()}
          onChange={onChangeValue}
          value={value}
        />
        <CaseSensitiveWrapper>
          <StyledCheckbox
            data-aid="stringMatchCondition-caseSensitive"
            checked={matcher.getCaseSensitive()}
            onChange={onChangeCaseSensitive}
            value=""
          />{' '}
          Case sensitive
        </CaseSensitiveWrapper>
      </Wrapper>
    </StyledRow>
  );

  function getPlaceholder() {
    if (matcher.getType() === MatcherType.Regex) {
      return 'Enter a regular expression';
    }

    return 'Enter comma-separated matches';
  }

  function renderCaseSensitiveText() {
    if (matcher.getCaseSensitive()) {
      return 'case sensitive';
    }
    return 'not case sensitive';
  }

  function renderContainsLabel() {
    const value = matcher.hasNot() ? Contains.False : Contains.True;
    const containsOrNot = find(containsOrNotOptions, { value });
    return containsOrNot ? containsOrNot.label : '';
  }

  function renderMatchLabel() {
    const value = matcher.getType();
    const match = find(matchEvaluatorOptions, { value });
    return match ? match.label : '';
  }
};

const StyledRow = styled(ConditionRow)`
  align-items: baseline;
`;

const Wrapper = styled.div`
  width: 100%;
`;

const SelectorRow = styled.div`
  align-items: center;
  display: flex;
  padding-bottom: 12px;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-right: 3px;
`;

const StyledContainsLabel = styled.span`
  margin-right: 8px;
`;

const StyledConditionValues = styled.div`
  margin-left: 40px;
  margin-bottom: 12px;
`;

const StyledCaseSensitive = styled.div`
  font-style: italic;
  margin-left: 40px;
`;

const CaseSensitiveWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  margin-top: 3px;
`;

function renderConditionLabel(condition: StringMatcherConditionType, conditionNames: object) {
  let key = condition.type as string;
  if (key === ConditionType.CustomAttribute) {
    key = (condition as CustomAttributeConditionType).name;
  } else if (key === ConditionType.AppPlatformDataAttribute) {
    key = (condition as AppPlatformDataAttributeStringConditionType).attrId;
  }
  return conditionNames[key];
}

type MatchEvaluatorProps = {
  matcher: StringMatcher;
  onChange: Function;
};

const MatchEvaluator: FC<MatchEvaluatorProps> = ({ matcher, onChange }) => {
  return (
    <StyledMatchDropdown leftMargin onSelect={onChange} options={matchEvaluatorOptions} value={matcher.getType()} />
  );
};

type ContainsOrNotSelectorProps = {
  contains: boolean;
  onChange: Function;
};

const ContainsOrNotSelector: FC<ContainsOrNotSelectorProps> = ({ contains, onChange }) => {
  return (
    <StyledMatchDropdown
      onSelect={onChange}
      options={containsOrNotOptions}
      value={contains ? Contains.True : Contains.False}
    />
  );
};

const StringMatcherInput = styled(InputField).attrs(() => ({ className: 'stringMatcherInput' }))`
  .stringMatcherInputError {
    display: none;
  }
`;

const StyledMatchDropdown = styled(DropdownMenu)`
  margin-left: ${p => (p.leftMargin ? '12px' : '0')};
  margin-right: 0;
  width: 120px;
`;

const defaultConditionNames = {
  [ConditionType.ChatURL]: 'Chat URL',
  [ConditionType.MessageBody]: 'Message Body',
  [ConditionType.MessageRecipient]: 'Message To',
  [ConditionType.MessageSender]: 'Message From',
  [ConditionType.MessageSubject]: 'Message Subject',
};

const matchEvaluatorOptions: { label: string; value: string }[] = [
  { label: 'Any', value: MatcherType.Contains },
  { label: 'Only', value: MatcherType.Exact },
  { label: 'Regex', value: MatcherType.Regex },
  { label: 'Words', value: MatcherType.Words },
];

// dropdown selection does not render when using false as a boolean
const containsOrNotOptions: { label: string; value: string }[] = [
  { label: 'contains', value: Contains.True },
  { label: 'does not contain', value: Contains.False },
];

function convertMatcherValueToInputString(value) {
  return Array.isArray(value) ? value.join(', ') : value;
}

function convertInputStringToMatcherValue(value, matcher) {
  const expectsArray = Array.isArray(matcher.getValue());
  return expectsArray ? value.split(', ') : value;
}

export default StringMatcherCondition;
